import { daysInMonth, parseDayKey, toDayKey, type DayKey } from './dayKey.js';

/* ===========================================================================
 * DAY-FIRST TEXT — `DD-MM-YYYY` in, `DD-MM-YYYY` out, and a reason when not.
 *
 * THE DEFECT THIS EXISTS TO END. A native `<input type="date">` takes its
 * display order from the reader's OPERATING SYSTEM, so the reports workspace
 * rendered `09 / 01 / 2026` on a screen where every table beside it wrote
 * `01-09-2026`. Same fact, two orders, one page — and which one you got
 * depended on a setting the app cannot see, cannot test and cannot state. The
 * app writes days FIRST everywhere else, so the picker writes days first too,
 * on every machine.
 *
 * TYPING IS NOT A FALLBACK. Clicking through twelve months to reach a birth
 * date is not a control, it is a punishment; the reader has to be able to type
 * `05-09-2026` and be believed. So the parser is deliberately lenient about
 * everything that carries no meaning — separators, single digits, surrounding
 * space — and refuses only what is genuinely impossible.
 *
 * AND IT NAMES THE PROBLEM. "31-09-2026" is not "invalid": it is a date whose
 * month has thirty days, and saying so is the difference between the reader
 * fixing it in one keystroke and the reader retyping the whole field guessing
 * at what offended it (design-rules §3.10 — state consequences, not
 * conditions). The MESSAGE is not built here, because this package has no
 * i18n: the failure is returned as data, and the call site's labels turn it
 * into a sentence in the reader's language.
 * ======================================================================== */

/** Why a typed date could not become a day key. */
export type DayFirstFailure =
  /** Nothing was typed. Not an error — the caller reads it as "cleared". */
  | { kind: 'empty' }
  /** Not three numbers in any spelling we accept. */
  | { kind: 'unreadable' }
  /** A month outside 1–12. */
  | { kind: 'month'; month: number }
  /** A real month, but that month is shorter than the day given. */
  | { kind: 'monthLength'; month: number; days: number };

export type DayFirstParse =
  | { ok: true; key: DayKey }
  | { ok: false; failure: DayFirstFailure };

/**
 * `'2026-09-05'` → `'05-09-2026'`. A value that is not a day key returns the
 * empty string rather than echoing itself: the trigger then falls back to its
 * placeholder, which is a truthful "nothing chosen" rather than a broken date
 * presented as a choice (design-rules §3.3).
 */
export function formatDayFirst(key: string | null | undefined): string {
  const p = parseDayKey(key);
  if (!p) return '';
  return `${String(p.day).padStart(2, '0')}-${String(p.month).padStart(2, '0')}-${String(p.year).padStart(4, '0')}`;
}

/** `'01-09-2026 – 06-09-2026'`, with an en dash and hair spaces around it. */
export function formatDayFirstRange(from: string, to: string): string {
  const a = formatDayFirst(from);
  const b = formatDayFirst(to);
  if (a && b) return `${a} – ${b}`;
  return a || b;
}

/* Three numbers separated by anything a keyboard offers between date parts —
   `-`, `/`, `.`, or a space. Nothing else: a parser that accepted arbitrary
   junk between the groups would read `5 people on 9` as a date. */
const PARTS_RE = /^(\d{1,4})\s*[-/.\s]\s*(\d{1,2})\s*[-/.\s]\s*(\d{1,4})$/;

/**
 * Read a typed date, day-first.
 *
 * THE ONE AMBIGUITY, RESOLVED BY WIDTH. `2026-09-05` is what the API speaks and
 * what a reader may well paste out of a URL, and it cannot be read day-first —
 * there is no 2026th of a month. A FOUR-DIGIT leading group is therefore taken
 * as a year and the string read big-endian; anything narrower is day-first. No
 * other input is ambiguous, because a day is never four digits.
 */
export function parseDayFirst(text: string): DayFirstParse {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, failure: { kind: 'empty' } };
  const m = PARTS_RE.exec(trimmed);
  if (!m) return { ok: false, failure: { kind: 'unreadable' } };

  const g1 = m[1] ?? '';
  const g2 = m[2] ?? '';
  const g3 = m[3] ?? '';
  const first = Number(g1);
  const middle = Number(g2);
  const last = Number(g3);
  const isoOrder = g1.length === 4;
  const year = isoOrder ? first : last;
  const month = middle;
  const day = isoOrder ? last : first;

  /* A year has to be written out. Two digits is a guess about a century, and a
     guess about a century on a document-expiry field is a compliance figure
     nobody typed. */
  if ((isoOrder ? g1 : g3).length !== 4) {
    return { ok: false, failure: { kind: 'unreadable' } };
  }
  if (month < 1 || month > 12) return { ok: false, failure: { kind: 'month', month } };
  const length = daysInMonth(year, month);
  if (day < 1 || day > length) {
    return { ok: false, failure: { kind: 'monthLength', month, days: length } };
  }
  return { ok: true, key: toDayKey(year, month, day) };
}
